import Phaser from 'phaser';
import type { Vec2 } from '../core/vec';

/**
 * Leafstorm Hambi's landing (spec 2026-09-26): where his tornado touches down,
 * a ring of autumn leaves spins round the spot and then spirals out to each
 * enemy the tornado caught. Theatre only — the sim has already dealt the
 * damage (`TowerSpec.tornado`); this is how the player SEES four enemies hit
 * by one shot.
 */

/** One leaf's on-screen size (px). */
const LEAF_DISPLAY = 14;
/** Leaves per enemy caught. */
const LEAVES_PER_TARGET = 3;
/** How long the leaves circle the landing spot before they fly out (ms). */
const SPIN_MS = 220;
/** Flight from the landing spot to an enemy (ms). */
const FLY_MS = 260;
/** Radius of the little whirl the leaves make at the landing spot (px). */
const WHIRL_R = 18;
/** Full turns each leaf makes while it flies. */
const LEAF_TURNS = 2;

export function spawnLeafWhirl(scene: Phaser.Scene, at: Vec2, targets: readonly Vec2[]): void {
  const aims = targets.length > 0 ? targets : [at];
  aims.forEach((to, ti) => {
    for (let i = 0; i < LEAVES_PER_TARGET; i++) {
      const start =
        ((ti * LEAVES_PER_TARGET + i) / (aims.length * LEAVES_PER_TARGET)) * Math.PI * 2;
      const leaf = scene.add
        .image(at.x + Math.cos(start) * WHIRL_R, at.y + Math.sin(start) * WHIRL_R, 'fx-leaf')
        .setDepth(3.6)
        .setDisplaySize(LEAF_DISPLAY, LEAF_DISPLAY)
        .setRotation(start);
      const orbit = { a: start };
      // Circle the landing spot…
      scene.tweens.add({
        targets: orbit,
        a: start + Math.PI * 2,
        duration: SPIN_MS,
        onUpdate: () => {
          if (!leaf.active) return;
          leaf.setPosition(at.x + Math.cos(orbit.a) * WHIRL_R, at.y + Math.sin(orbit.a) * WHIRL_R);
          leaf.rotation += 0.4;
        },
        onComplete: () => {
          if (!leaf.active) return;
          // …then spiral out to the enemy, spinning, and fade as it arrives.
          scene.tweens.add({
            targets: leaf,
            x: to.x + (i - 1) * 6,
            y: to.y + (i - 1) * 4,
            rotation: leaf.rotation + Math.PI * 2 * LEAF_TURNS,
            alpha: { from: 1, to: 0 },
            duration: FLY_MS,
            delay: i * 40,
            ease: 'Quad.easeOut',
            onComplete: () => leaf.destroy(),
          });
        },
      });
    }
  });
}
